import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '@wix/da-shared-react/pkg/Dropdown/MenuPreset/StandardMenu';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import UnderlinedTab from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs/UnderlinedTab';
import type { TabBarProps } from '../../types';
import { useScrollToActiveItem } from '../../hooks/useScrollToActiveItem';
import { useIsOverflowedHorizontal } from '../../hooks/useIsOverflowedHorizontal';
import s from './TabBar.scss';

export type Props = Pick<TabBarProps, 'items' | 'activeMenuItem' | 'className'>;

const DESKTOP_MAX_VISIBLE_ITEMS = 5;

function getMenuItems(
  items: NonNullable<Props['items']>,
  activeMenuItem: Props['activeMenuItem'],
  isMobile: boolean
) {
  if (isMobile) {
    return [items, []];
  }

  // If an item that would normally be in the overflow menu is active,
  // un-overflow it and swap out the last non-overflowed item
  if (
    activeMenuItem &&
    items
      .slice(DESKTOP_MAX_VISIBLE_ITEMS)
      .map(item => item.subnav)
      .includes(activeMenuItem)
  ) {
    const visible = items
      .slice(0, DESKTOP_MAX_VISIBLE_ITEMS - 1)
      .concat(items.filter(item => item.subnav === activeMenuItem));
    return [visible, items.filter(item => !visible.includes(item))];
  } else {
    return [
      items.slice(0, DESKTOP_MAX_VISIBLE_ITEMS),
      items.slice(DESKTOP_MAX_VISIBLE_ITEMS),
    ];
  }
}

const TabBar: React.FC<Props> = ({
  items = [],
  activeMenuItem,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { navListRef, activeItemRef } = useScrollToActiveItem(isMobile, 16);
  const { isOverflowed } = useIsOverflowedHorizontal(navListRef);

  const [actualItems, overflowItems] = useMemo(() => {
    const [visible, overflow] = getMenuItems(items, activeMenuItem, isMobile);
    return [
      visible.map(menuItem => ({
        index: menuItem.subnav,
        label: t(`browseHeader.page.${menuItem.subnav}`),
        href: menuItem.url,
        hasNotification: menuItem.hasNewContent,
        biData: {
          evid: BiEventType.CONTENT_NAVBAR_CLICK,
          link_name: menuItem.subnav,
          indicator: menuItem.hasNewContent ? 1 : 0,
        },
      })),
      overflow.map(menuItem => ({
        link: menuItem.url,
        label: t(`browseHeader.page.${menuItem.subnav}`),
        biData: {
          evid: BiEventType.CONTENT_NAVBAR_CLICK,
          link_name: menuItem.subnav,
        },
      })),
    ];
  }, [activeMenuItem, isMobile, items, t]);

  const setActiveItemRef = (node: HTMLLIElement) => {
    activeItemRef.current = node;
  };

  return (
    <div
      className={classnames(
        s['root'],
        isOverflowed && s['scrollable'],
        className
      )}
      ref={navListRef}
    >
      {children}
      {!children && actualItems.length > 0 && (
        <div className={s['nav-list']}>
          <UnderlinedTabs
            className={s['tabs']}
            size={isMobile ? 'small' : 'big'}
            items={actualItems}
            activeIndex={activeMenuItem}
            activeItemRef={setActiveItemRef}
            extraTabs={
              <>
                {overflowItems.length > 0 && (
                  <UnderlinedTab
                    className={s['more-menu']}
                    label={t('browseHeader.more')}
                    postfix={
                      <StandardMenu
                        lightText
                        id="subnav-more"
                        className={s['more-list']}
                        items={overflowItems}
                      />
                    }
                  />
                )}
              </>
            }
          />
        </div>
      )}
    </div>
  );
};
TabBar.displayName = 'TabBar';

export default TabBar;
